import { Box, Typography } from '@mui/material';
import React, { forwardRef, useCallback, useState } from 'react';

export interface VirtualizedListboxProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  itemHeight?: number;
  maxHeight?: number;
  overscanCount?: number;
}

export const VirtualizedListbox = forwardRef<HTMLDivElement, VirtualizedListboxProps>(
  function VirtualizedListbox(
    { children, itemHeight = 32, maxHeight = 400, overscanCount = 5, ...props },
    ref
  ) {
    // State for scroll-based virtualization (must be at top level)
    const [scrollTop, setScrollTop] = useState(0);

    // Handle scroll events (must be at top level)
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      setScrollTop(e.currentTarget.scrollTop);
    }, []);

    // Convert children to array
    const childrenArray = React.Children.toArray(children);

    // If no children, show empty state
    if (childrenArray.length === 0) {
      return (
        <Box ref={ref} sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No options available
          </Typography>
        </Box>
      );
    }

    // Calculate list height
    const listHeight = Math.min(childrenArray.length * itemHeight, maxHeight);

    // If we have fewer items than would fit in the viewport, don't virtualize
    const shouldVirtualize = childrenArray.length > Math.floor(maxHeight / itemHeight);

    if (!shouldVirtualize) {
      return (
        <div
          ref={ref}
          {...props}
          style={{
            width: '100%',
            maxHeight: maxHeight,
            overflow: 'auto',
            ...props.style,
          }}
        >
          {children}
        </div>
      );
    }

    // Calculate visible range
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(
      childrenArray.length - 1,
      startIndex + Math.ceil(listHeight / itemHeight) + overscanCount
    );

    // Calculate visible items
    const visibleChildren = childrenArray.slice(startIndex, endIndex + 1);
    return (
      <div
        ref={ref}
        {...props}
        style={{
          ...props.style,
          width: '100%',
          height: listHeight,
          overflow: 'auto',
          padding: 0,
          margin: 0,
        }}
        onScroll={handleScroll}
      >
        {/* Virtual spacer for items before visible range */}
        {startIndex > 0 && <div style={{ height: startIndex * itemHeight }} />}

        {/* Render visible items */}
        {visibleChildren.map((child, index) => (
          <div
            key={`virtual-item-${startIndex + index}`}
            style={{
              height: itemHeight,
              minHeight: itemHeight,
              width: '100%',
              margin: 0,
              padding: 0,
              display: 'contents',
            }}
          >
            {child}
          </div>
        ))}

        {/* Virtual spacer for items after visible range */}
        {endIndex < childrenArray.length - 1 && (
          <div style={{ height: (childrenArray.length - 1 - endIndex) * itemHeight }} />
        )}
      </div>
    );
  }
);

VirtualizedListbox.displayName = 'VirtualizedListbox';

export default VirtualizedListbox;
